/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useMemo } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  AllReportYearsBase,
  ReportAPIResponse,
  VariablesForCountFilter,
} from './api';
import { ReportGroups, getReportGroupName } from './utils';
import { faEyes } from '@fortawesome/pro-light-svg-icons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GridTableRowProps } from 'shared/components/Grid/GridTableRow';
import {
  FeedbackStatus,
  ReportStage,
} from 'donor/portal/pages/FeedbackDashboard/api';
import AddNewButton from './AddNewButton';
import AddNewModals from './AddNewModals';
import { Program } from '../Reports/NewReportDialog';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import ProgramEditButton from './ProgramEditButton';
import { NPOUser } from './NewProgram/api';
import TrueImpactBadge from 'shared/design-system/components/TrueImpactBadge';
import { AllReportStage } from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { isDatePassed } from 'shared/utils/date';
import { FeedbackType } from '../NPOFeedback/SubmitReady/utils';
import { getReportVisibilityText } from 'shared/account/pages/reportSharing/ReportSharingToggle';
import TrueImpactTooltip from 'shared/account/components/CustomTooltip/TrueImpactTooltip';
import { UserProgramReport } from '../ProgramsAndReports/types';

export const getReportGroupKey = (
  item: ReportGroups,
  index: number,
  extra: VariablesForCountFilter,
) => index;

type ExtraData = {
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  reloadProgramList: () => unknown;
};

export const getGroupItemRender = (
  item: ReportGroups,
  index: number,
  isActive: boolean,
  extra: VariablesForCountFilter & ExtraData,
  programId: number,
  onPressDelete: (programName: string) => void,
) => {
  switch (extra.filter) {
    case 'Program': {
      const programName = getReportGroupName(item).toString();
      return (
        <>
          <TrueImpactText
            variant="body"
            className="nav-item-name"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <strong>{programName}</strong> ({item.count})
            {item.count === 0 && (
              <TrueImpactIconButton
                label={`Delete '${programName}'`}
                icon={faTrash as any}
                onClick={e => {
                  e.stopPropagation();
                  onPressDelete?.(programName);
                }}
              />
            )}
          </TrueImpactText>
          <ProgramEditButton
            isActive={isActive}
            reloadProgramList={extra.reloadProgramList}
            allNPOUsers={extra.allNPOUsers}
            programsList={extra.programsList}
            programId={programId}
            programName={programName}
          />
        </>
      );
    }
    case 'Year':
    case 'Donor':
    case 'Manager':
    default:
      return (
        <TrueImpactText variant="body" className="nav-item-name">
          <strong>{getReportGroupName(item)}</strong> ({item.count})
        </TrueImpactText>
      );
  }
};

interface RenderEmptyReportStateProps {
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  reloadProgramList: () => unknown;
}

export const RenderEmptyReportState: React.FC<RenderEmptyReportStateProps> = ({
  allNPOUsers,
  programsList,
  reloadProgramList,
}) => {
  const [openNewReport, setOpenNewReport] = useState<boolean>(false);
  const [openNewProgram, setOpenNewProgram] = useState<boolean>(false);

  const onNewReportClick = useCallback(() => setOpenNewReport(true), [
    setOpenNewReport,
  ]);
  const onNewProgramClick = useCallback(() => setOpenNewProgram(true), [
    setOpenNewProgram,
  ]);

  const addButtons = useMemo(
    () => [
      {
        text: 'New Report',
        onClick: onNewReportClick,
      },
      {
        text: 'New Program',
        onClick: onNewProgramClick,
      },
    ],
    [onNewReportClick, onNewProgramClick],
  );
  return (
    <div className="empty-state">
      <FontAwesomeIcon icon={faEyes} className="icon" />
      <TrueImpactText variant="body" align="center">
        It looks like you don’t have any reports for your filters.
      </TrueImpactText>
      <TrueImpactText variant="body" align="center">
        If you’re looking for another year or program, you can change the filter
        above, or add a new report and program now.
      </TrueImpactText>
      <AddNewButton onAdd={addButtons} />
      <AddNewModals
        allNPOUsers={allNPOUsers}
        programsList={programsList}
        openNewProgram={openNewProgram}
        openNewReport={openNewReport}
        reloadProgramList={reloadProgramList}
        setOpenNewProgram={setOpenNewProgram}
        setOpenNewReport={setOpenNewReport}
        handleReportCountLoad={undefined}
        defaultProgramName={undefined}
      />
    </div>
  );
};

const getErrorsAnWarnings = (errors: any, warnings: any) => {
  let response = '';
  if (errors === 0 && warnings === 0) {
    return 'None';
  }
  if (errors > 0) {
    response += `${errors.length} error${errors.length === 1 ? '' : 's'}`;
  }
  if (warnings > 0) {
    if (response !== '') response += ' ';
    response += `${warnings.length} warning{warnings.length===1?'':'s'}`;
  }
  return response;
};

export type ReportsTable = ReportAPIResponse & {
  id: number;
  version?: string | null;
};

export const isV3Report = (report: ReportsTable): boolean =>
  report.version !== 'v4';

const getStageText = (stage: ReportStage): string => {
  switch (stage) {
    case 'INITIAL':
      return 'Initial Forecast';
    case 'INTERIM':
      return 'Interim Update';
    case 'FINAL':
      return 'Final Report';
    default:
      return stage;
  }
};

export const getStageGenericText = (stage: AllReportStage): string => {
  switch (stage) {
    case 'INITIAL':
      return 'Initial';
    case 'INTERIM':
      return 'Midpoint';
    case 'FINAL':
      return 'Final';
    default:
      return stage;
  }
};

export const getReportName = (
  report: ReportsTable | AllReportYearsBase | UserProgramReport,
) => {
  if (!report.name) {
    return 'Not set';
  }
  if (/\(\d{4}\)/.test(report.name)) {
    return report.name;
  }
  return `${report.name} (${report.year})`;
};

export const getReportNameString = (reportName: string) => {
  return reportName ? reportName.replace('(V4 Clone)', '') : 'Not set';
};

export const getReportStage = (stage: ReportStage) => {
  switch (stage) {
    case 'INITIAL':
      return 'Initial Forecast';
    case 'INTERIM':
      return 'Interim Update';
    case 'FINAL':
      return 'Final Report';
    default:
      return stage;
  }
};

const getReportState = (
  report:
    | ReportsTable
    | {
        feedbackType: FeedbackType;
        stage: ReportStage;
        status: FeedbackStatus;
        endDate: string;
      },
) => {
  const isReportPassed = isDatePassed(report.endDate);
  const status = report.status;
  const feedbackType = report.feedbackType;
  const includesDonor =
    feedbackType === 'Donor Only' || feedbackType === 'Full (TI + Donor)';
  switch (report.stage) {
    case 'INITIAL':
      switch (status) {
        case 'Draft':
          return isReportPassed
            ? 'Final Updates Needed'
            : 'Awaiting Initial Draft';
        case 'TI Feedback Provided':
          return includesDonor ? 'Awaiting Funder Review' : 'Feedback Provided';
        case 'Funder Feedback Provided':
          return 'Feedback Provided';
        case 'Published':
          return 'Completed Forecast';
        default:
          return status;
      }
    case 'INTERIM':
      switch (status) {
        case 'Draft':
          return 'Interim Update Needed';
        case 'TI Feedback Provided':
          return includesDonor ? 'Awaiting Funder Review' : 'Feedback Provided';
        case 'Funder Feedback Provided':
          return 'Feedback Provided';
        case 'Published':
          return 'Completed Interim Forecast';
        default:
          return status;
      }
    case 'FINAL':
      switch (status) {
        case 'Draft':
          return 'Final Updates Needed';
        case 'TI Feedback Provided':
          return includesDonor ? 'Awaiting Funder Review' : 'Feedback Provided';
        case 'Funder Feedback Provided':
          return 'Feedback Provided';
        case 'Published':
          return 'Finalized';
        default:
          return status;
      }
  }
};

const publicVisibilityTooltip = (
  <TrueImpactText variant="body">
    This report is visible to other funders
  </TrueImpactText>
);
const privateVisibilityTooltip = (
  <TrueImpactText variant="body">This report is restricted</TrueImpactText>
);

const style: React.CSSProperties = {
  display: 'flex',
  textAlign: 'left',
  alignItems: 'center',
};
export const reportHeaders: GridTableRowProps<ReportsTable>['headers'] = [
  {
    text: 'Program Name',
    Render: ({ row }) => (
      <td style={style}>
        {getReportName(row)}
        {isV3Report(row) && <TrueImpactBadge>legacy</TrueImpactBadge>}
      </td>
    ),
  },
  { text: 'Stage', Render: ({ row }) => <td>{getStageText(row.stage)}</td> },
  { text: 'Status', Render: ({ row }) => <td>{row.status}</td> },
  {
    text: 'Donation',
    Render: ({ row }) => <td>{`$${numberWithCommas(row.budget || 0)}`}</td>,
  },
  // {
  //   text: 'Donated',
  //   Render: ({ row }) => <td>{calculateDonationPercent(row)}</td>,
  // },
  // {
  //   text: 'Year',
  //   Render: ({ row }) => <td>{row.year}</td>,
  // },
  {
    text: 'Donor',
    Render: ({ row }) => <td>{row.donorName || '-'}</td>,
  },
  {
    text: 'Manager',
    Render: ({ row }) => <td>{row.manager || '-'}</td>,
  },
  // {
  //   text: 'Donations',
  //   Render: ({ row }) => (
  //     <td>{`$${numberWithCommas(row.totalDonations || 0)}`}</td>
  //   ),
  // },
  // { text: 'Next To Do', Render: ({ row }) => <td>-</td> },
  // {
  //   text: 'Errors & warning',
  //   Render: ({ row }) => (
  //     <td>{getErrorsAnWarnings(row.errors, row.warnings)}</td>
  //   ),
  // },
];

export const reportingTypeHeader: GridTableRowProps<
  ReportsTable
>['headers'][number] = {
  text: 'Reporting Type',
  Render: ({ row }) => (
    <td>
      {row.reportingType === 'LIGHT'
        ? 'Impact Snapshot'
        : row.reportingType === 'SUPER_LIGHT'
        ? 'Impact Snapshot (Lite)'
        : 'Full Reporting'}
    </td>
  ),
};

export const reportVisibilityHeader: GridTableRowProps<
  ReportsTable
>['headers'][number] = {
  text: 'Visibility',
  // Render: item => <ReportSharingColumn item={item} />,
  Render: ({ row }) => (
    <td>
      <TrueImpactTooltip
        title={
          row.visibility === 'PUBLIC'
            ? publicVisibilityTooltip
            : privateVisibilityTooltip
        }
      >
        {getReportVisibilityText(row)}
      </TrueImpactTooltip>
    </td>
  ),
};
